/* eslint-disable react/prop-types */
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Lock, LogOut, ShieldCheck, User } from "lucide-react";

import Header from "@/components/landing/Header";

/**
 * Cookie-based admin authentication gate.
 *
 * Replaces the legacy `X-Admin-Token` localStorage scheme with a proper
 * session-cookie flow (see `backend/admin_auth.py`):
 *   • Username + bcrypt-checked password
 *   • Mandatory TOTP 2FA (Google Authenticator)
 *   • Signed HttpOnly cookie, 8h normal session or 30 days with "remember me"
 *
 * All admin pages get the cookie automatically because we set
 * `axios.defaults.withCredentials = true` in `index.js`. Legacy
 * X-Admin-Token headers on existing admin pages are simply ignored by the
 * backend — no need to refactor every call site.
 *
 * Backwards-compat: `useAdminToken()` still works but returns `token: ""`
 * (no header to send). It's kept so older imports keep compiling.
 */
const API = process.env.REACT_APP_BACKEND_URL;

const AdminAuthContext = createContext({
  user: null,
  loading: true,
  logout: async () => {},
  // legacy compat
  token: "",
  setToken: () => {},
  clearToken: () => {},
});

export const useAdminAuth = () => useContext(AdminAuthContext);
// Legacy alias — children that destructure `token` will simply receive ""
export const useAdminToken = () => {
  const ctx = useContext(AdminAuthContext);
  return { token: "", setToken: () => {}, clearToken: ctx.logout };
};

function formatErrorDetail(detail) {
  if (detail == null) return "Er ging iets mis. Probeer opnieuw.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || JSON.stringify(e)).join(" · ");
  return String(detail);
}

// ── Forgot-password sub-form ─────────────────────────────────────────────
function ForgotPasswordForm({ onBack }) {
  const [username, setUsername] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim()) return;
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/api/admin/auth/forgot-password`, {
        username: username.trim(),
      });
      setDone(true);
    } catch (err) {
      setError(formatErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="admin-forgot-success"
        className="mt-8 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-6 text-[13px] leading-relaxed text-[hsl(var(--ink-soft))]"
      >
        <h2 className="mb-2 text-base font-semibold text-[hsl(var(--ink))]">
          Controleer je e-mail
        </h2>
        <p>
          Als er een account met deze gebruikersnaam bestaat, is er een e-mail
          verstuurd met een link om je wachtwoord opnieuw in te stellen. De link
          is <strong>60 minuten</strong> geldig.
        </p>
        <button
          type="button"
          onClick={onBack}
          data-testid="admin-forgot-back"
          className="mt-4 text-[12px] underline"
        >
          ← Terug naar inloggen
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="admin-forgot-form"
      className="mt-8 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-6"
    >
      <h2 className="text-base font-semibold text-[hsl(var(--ink))]">
        Wachtwoord vergeten?
      </h2>
      <p className="mt-1 text-[12px] text-[hsl(var(--ink-soft))]">
        Vul je gebruikersnaam in — we sturen je een reset-link per e-mail.
      </p>
      <label className="mt-4 block">
        <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))]">
          <User className="h-3 w-3" /> Gebruikersnaam
        </span>
        <input
          type="email"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="support@equisight.nl"
          autoFocus
          data-testid="admin-forgot-username"
          className="mt-2 w-full rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-3 text-sm focus:border-[hsl(var(--forest))] focus:outline-none"
          autoComplete="username"
          required
        />
      </label>
      {error && (
        <p data-testid="admin-forgot-error" className="mt-3 text-[12px] text-rose-700">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={busy || !username.trim()}
        data-testid="admin-forgot-submit"
        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[hsl(var(--forest))] px-5 py-3 text-sm font-semibold text-[hsl(var(--background))] transition-all hover:bg-[hsl(var(--forest-dark))] disabled:opacity-40"
      >
        {busy ? "Versturen…" : "Stuur reset-link"}
      </button>
      <button
        type="button"
        onClick={onBack}
        className="mt-3 block w-full text-[12px] text-[hsl(var(--ink-soft))] underline"
      >
        ← Terug naar inloggen
      </button>
    </form>
  );
}

// ── Login form ───────────────────────────────────────────────────────────
function LoginForm({ onSuccess }) {
  const [stage, setStage] = useState("credentials"); // credentials | totp | totpSetup | forgot
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [challenge, setChallenge] = useState(null); // {challenge_token, totp_secret?, totp_qr_data_uri?}
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submitCredentials = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/api/admin/auth/login`, {
        username: username.trim(),
        password,
      });
      setChallenge(data);
      setStage(data.stage === "totp_setup_required" ? "totpSetup" : "totp");
    } catch (err) {
      setError(formatErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const submitTotp = async (e) => {
    e.preventDefault();
    if (!code.trim() || !challenge) return;
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/api/admin/auth/totp/verify`, {
        challenge_token: challenge.challenge_token,
        code: code.trim(),
        remember_me: rememberMe,
      });
      // Cookie is now set by the server — fetch /me to confirm
      const { data } = await axios.get(`${API}/api/admin/auth/me`);
      onSuccess(data);
    } catch (err) {
      setError(formatErrorDetail(err?.response?.data?.detail) || err.message);
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  // ── Stage 0 — forgot password ──────────────────────────────────────
  if (stage === "forgot") {
    return <ForgotPasswordForm onBack={() => setStage("credentials")} />;
  }

  // ── Stage 1 — username + password ───────────────────────────────────
  if (stage === "credentials") {
    return (
      <form
        onSubmit={submitCredentials}
        className="mt-8 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-6"
        data-testid="admin-login-credentials"
      >
        <label className="block">
          <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))]">
            <User className="h-3 w-3" /> Gebruikersnaam
          </span>
          <input
            type="email"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="support@equisight.nl"
            autoFocus
            data-testid="admin-username-input"
            className="mt-2 w-full rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-3 text-sm focus:border-[hsl(var(--forest))] focus:outline-none"
            autoComplete="username"
            required
          />
        </label>
        <label className="mt-4 block">
          <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))]">
            <Lock className="h-3 w-3" /> Wachtwoord
          </span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            data-testid="admin-password-input"
            className="mt-2 w-full rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-3 text-sm focus:border-[hsl(var(--forest))] focus:outline-none"
            autoComplete="current-password"
            required
          />
        </label>
        <label className="mt-4 flex items-center gap-2 text-[12px] text-[hsl(var(--ink-soft))]">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            data-testid="admin-remember-me"
            className="h-4 w-4 rounded border-[hsl(var(--border))]"
          />
          Onthoud deze browser 30 dagen
        </label>
        {error && (
          <p data-testid="admin-login-error" className="mt-3 text-[12px] text-rose-700">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy || !username.trim() || !password}
          data-testid="admin-login-submit"
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[hsl(var(--forest))] px-5 py-3 text-sm font-semibold text-[hsl(var(--background))] transition-all hover:bg-[hsl(var(--forest-dark))] disabled:opacity-40"
        >
          {busy ? "Bezig…" : "Volgende"}
        </button>
        <button
          type="button"
          onClick={() => setStage("forgot")}
          data-testid="admin-forgot-link"
          className="mt-3 block w-full text-center text-[12px] text-[hsl(var(--ink-soft))] underline"
        >
          Wachtwoord vergeten?
        </button>
      </form>
    );
  }

  // ── Stage 2a — TOTP setup (first-time enrollment) ───────────────────
  if (stage === "totpSetup") {
    return (
      <form
        onSubmit={submitTotp}
        className="mt-8 space-y-5 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-6"
        data-testid="admin-login-totp-setup"
      >
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))]">2FA instellen — eenmalig</p>
          <h2 className="mt-1 text-lg font-semibold text-[hsl(var(--ink))]">
            Scan deze QR-code met Google Authenticator
          </h2>
          <p className="mt-1 text-[12px] text-[hsl(var(--ink-soft))]">
            Open Google Authenticator (of 1Password / Authy) → <strong>+</strong> →{" "}
            <strong>QR-code scannen</strong>.
          </p>
        </div>
        {challenge?.totp_qr_data_uri && (
          <div className="flex justify-center">
            <img
              src={challenge.totp_qr_data_uri}
              alt="Google Authenticator QR-code"
              data-testid="admin-totp-qr"
              className="h-56 w-56 rounded-lg border border-[hsl(var(--border))] bg-white p-2"
            />
          </div>
        )}
        <details className="text-[12px] text-[hsl(var(--ink-soft))]">
          <summary className="cursor-pointer underline">Kan QR niet scannen? Toon code voor handmatige invoer</summary>
          <code
            data-testid="admin-totp-secret"
            className="mt-2 block break-all rounded-lg bg-[hsl(var(--background))] p-3 font-mono text-[11px]"
          >
            {challenge?.totp_secret}
          </code>
        </details>
        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))]">
            6-cijferige code uit de app
          </span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="123456"
            autoFocus
            data-testid="admin-totp-input"
            className="mt-2 w-full rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-3 text-center text-2xl font-mono tracking-[0.4em] focus:border-[hsl(var(--forest))] focus:outline-none"
            autoComplete="one-time-code"
            required
          />
        </label>
        {error && (
          <p data-testid="admin-totp-error" className="text-[12px] text-rose-700">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy || code.length !== 6}
          data-testid="admin-totp-submit"
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[hsl(var(--forest))] px-5 py-3 text-sm font-semibold text-[hsl(var(--background))] transition-all hover:bg-[hsl(var(--forest-dark))] disabled:opacity-40"
        >
          {busy ? "Verifiëren…" : "2FA activeren & inloggen"}
        </button>
      </form>
    );
  }

  // ── Stage 2b — TOTP verify (existing enrollment) ────────────────────
  return (
    <form
      onSubmit={submitTotp}
      className="mt-8 space-y-5 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-6"
      data-testid="admin-login-totp"
    >
      <div>
        <p className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))]">2FA</p>
        <h2 className="mt-1 text-lg font-semibold text-[hsl(var(--ink))]">
          Voer de 6-cijferige code in
        </h2>
        <p className="mt-1 text-[12px] text-[hsl(var(--ink-soft))]">Uit Google Authenticator.</p>
      </div>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={6}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        placeholder="123456"
        autoFocus
        data-testid="admin-totp-input"
        className="w-full rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-3 text-center text-2xl font-mono tracking-[0.4em] focus:border-[hsl(var(--forest))] focus:outline-none"
        autoComplete="one-time-code"
        required
      />
      {error && (
        <p data-testid="admin-totp-error" className="text-[12px] text-rose-700">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={busy || code.length !== 6}
        data-testid="admin-totp-submit"
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[hsl(var(--forest))] px-5 py-3 text-sm font-semibold text-[hsl(var(--background))] transition-all hover:bg-[hsl(var(--forest-dark))] disabled:opacity-40"
      >
        {busy ? "Verifiëren…" : "Inloggen"}
      </button>
    </form>
  );
}

// ── Main gate ────────────────────────────────────────────────────────────
export default function AdminAuthGate({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount: check if there's already a valid session cookie
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/api/admin/auth/me`)
      .then(({ data }) => { if (!cancelled) setUser(data); })
      .catch(() => { if (!cancelled) setUser(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/api/admin/auth/logout`); } catch { /* ignore */ }
    setUser(null);
  }, []);

  if (loading) {
    return (
      <main className="min-h-screen bg-[hsl(var(--background))]">
        <Header />
        <div className="mx-auto max-w-md px-6 py-20 text-center text-[12px] text-[hsl(var(--ink-soft))]">
          Sessie controleren…
        </div>
      </main>
    );
  }

  if (user) {
    return (
      <AdminAuthContext.Provider
        value={{ user, loading, logout, token: "", setToken: () => {}, clearToken: logout }}
      >
        {children}
      </AdminAuthContext.Provider>
    );
  }

  return (
    <main data-testid="admin-auth-gate" className="min-h-screen bg-[hsl(var(--background))]">
      <Header />
      <section className="mx-auto max-w-md px-6 py-20">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--ink))]"
        >
          ← Terug naar homepage
        </Link>
        <div className="mt-8 flex items-center gap-3">
          <ShieldCheck className="h-8 w-8 text-[hsl(var(--forest))]" strokeWidth={1.6} />
          <div>
            <h1 className="font-display text-3xl text-[hsl(var(--ink))]">Admin paneel</h1>
            <p className="text-[12px] text-[hsl(var(--ink-soft))]">EquiSight backstage · 2FA verplicht</p>
          </div>
        </div>

        <LoginForm onSuccess={setUser} />

        <p className="mt-4 text-[10px] text-[hsl(var(--ink-soft))]">
          Sessie wordt opgeslagen in een HttpOnly cookie (8 uur normaal, 30 dagen met &quot;onthouden&quot;).
        </p>
      </section>
    </main>
  );
}

/** Reusable logout button — render in any admin page top-right. */
export const AdminLogoutButton = ({ className = "" }) => {
  const { logout, user } = useAdminAuth();
  return (
    <button
      onClick={logout}
      data-testid="admin-logout"
      title={user?.username ? `Ingelogd als ${user.username}` : ""}
      className={`inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-1.5 text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--ink))] ${className}`}
    >
      <LogOut className="h-3 w-3" /> Uitloggen
    </button>
  );
};
